import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { logCtaRedirect, type CtaContext } from "@/lib/cta-analytics";
import { getOnboardingDestination, getCtaContext } from "@/lib/cta.functions";
import { setAuthLoading } from "@/components/AuthLoadingOverlay";

export const Route = createFileRoute("/_authenticated/dashboard")({
  component: DashboardRedirect,
});

function DashboardRedirect() {
  const navigate = useNavigate();
  const fetchDestination = useServerFn(getOnboardingDestination);
  const fetchCookieCtx = useServerFn(getCtaContext);

  useEffect(() => {
    let cancelled = false;
    // Whenever the redirect page is visited directly (deep link, refresh,
    // signup path), cover the screen with the global overlay so no
    // _authenticated route-level placeholder can flash.
    setAuthLoading(true);
    (async () => {
      const [cookieRes, destRes] = await Promise.allSettled([
        fetchCookieCtx(),
        fetchDestination(),
      ]);
      if (cancelled) return;

      let ctx: CtaContext = {};
      if (cookieRes.status === "fulfilled" && cookieRes.value?.context) {
        const c = cookieRes.value.context;
        ctx = {
          token: c.token,
          campaign: c.campaign,
          shift: c.shift_id,
          postcode: c.postcode,
          radius: c.radius_km,
          ref: c.ref,
        };
      }

      const { data: u } = await supabase.auth.getSession();
      if (cancelled) return;
      const user = u.session?.user;
      if (!user) {
        navigate({ to: "/auth", replace: true });
        return;
      }

      if (destRes.status === "fulfilled" && destRes.value?.to) {
        const dest = destRes.value;
        logCtaRedirect({
          stage: "dashboard",
          from: "/dashboard",
          to: dest.to,
          reason: dest.reason,
          role: dest.role,
          onboarding_status: dest.onboarding_status,
          user_id: user.id,
          context: ctx,
        });
        navigate({ to: dest.to as never, replace: true });
        return;
      }

      const msg = destRes.status === "rejected"
        ? (destRes.reason instanceof Error ? destRes.reason.message : "unknown-error")
        : "no-destination";
      logCtaRedirect({
        stage: "dashboard",
        from: "/dashboard",
        to: "/bartender",
        reason: `fallback:${msg}`,
        user_id: user.id,
        context: ctx,
      });
      navigate({ to: "/bartender", replace: true });
    })();
    return () => {
      cancelled = true;
      // Cleared when the destination route has mounted and is unmounting
      // /dashboard. By this point the destination's first paint is on
      // screen, so dropping the overlay is seamless.
      setAuthLoading(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // The page itself renders nothing — the overlay covers the viewport.
  return null;
}

